import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import SettingsGear from '../components/SettingsGear';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { 
  LogOut, Book, Pencil, PenTool, Highlighter, Brush, Eraser, 
  Trash2, Download, Save, Plus, Palette, RotateCcw, RectangleVertical, RectangleHorizontal, Award, Trophy
} from 'lucide-react';

// Use relative URLs in production, full URL only in preview
const API_URL = window.location.hostname.includes('preview.emergentagent.com')
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';
axios.defaults.withCredentials = true;

// Drawing tools configuration
const TOOLS = [
  { id: 'pen', name: 'Pen', icon: PenTool, lineWidth: 2, opacity: 1, lineCap: 'round' },
  { id: 'marker', name: 'Marker', icon: Highlighter, lineWidth: 8, opacity: 0.7, lineCap: 'round' },
  { id: 'crayon', name: 'Crayon', icon: Pencil, lineWidth: 12, opacity: 0.6, lineCap: 'round' },
  { id: 'brush', name: 'Brush', icon: Brush, lineWidth: 16, opacity: 0.8, lineCap: 'round' },
  { id: 'eraser', name: 'Eraser', icon: Eraser, lineWidth: 20, opacity: 1, lineCap: 'round' },
];

// Preset colors
const COLORS = [
  '#2D3436', '#636E72', '#FF6B6B', '#FF8E53', '#FFD93D', 
  '#6EE7B7', '#38BDF8', '#818CF8', '#C084FC', '#F472B6',
  '#FFFFFF', '#FFF9E6', '#E8F8F0', '#EBF5FB', '#F3E8FF'
];

// Background colors
const BG_COLORS = [
  '#FFFFFF', '#FFF9E6', '#FEF3C7', '#DCFCE7', '#CFFAFE', 
  '#E0E7FF', '#FAE8FF', '#FCE7F3', '#FEE2E2', '#F3F4F6'
];

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_mood-diary-calendar/artifacts/sd96v0rx_image.png';

// Paint bucket icon — no spill (idle)
const BucketIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 11L7 21a1 1 0 001 1h8a1 1 0 001-1l1.5-10" />
    <rect x="4" y="9" width="16" height="2.5" rx="1" />
    <path d="M9.5 9V6.5a2.5 2.5 0 015 0V9" />
  </svg>
);

// Paint bucket icon — with paint drip (active)
const BucketSpillIcon = ({ className, spillColor }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 11L7 21a1 1 0 001 1h8a1 1 0 001-1l1.5-10" />
    <rect x="4" y="9" width="16" height="2.5" rx="1" />
    <path d="M9.5 9V6.5a2.5 2.5 0 015 0V9" />
    <path d="M19 11.5c1.2 1.2 2 3.2 1.5 5.5" stroke={spillColor || "currentColor"} strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="20" cy="18.5" r="1" fill={spillColor || "currentColor"} stroke="none" />
  </svg>
);

// Flood fill algorithm (scanline) — respects drawn line boundaries
function floodFill(canvas, startX, startY, fillColorHex) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  const w = canvas.width;
  const h = canvas.height;

  startX = Math.round(startX);
  startY = Math.round(startY);
  if (startX < 0 || startX >= w || startY < 0 || startY >= h) return;

  // Sample the color at the click point
  const si = (startY * w + startX) * 4;
  const sr = data[si], sg = data[si + 1], sb = data[si + 2], sa = data[si + 3];

  // Parse fill color
  const fr = parseInt(fillColorHex.slice(1, 3), 16);
  const fg = parseInt(fillColorHex.slice(3, 5), 16);
  const fb = parseInt(fillColorHex.slice(5, 7), 16);

  // Don't fill if clicking on (nearly) the same color
  if (Math.abs(sr - fr) < 6 && Math.abs(sg - fg) < 6 && Math.abs(sb - fb) < 6 && sa > 240) return;

  // Very tight tolerance for color matching — prevents leaking through anti-aliased edges
  const tol = 10;
  const match = (i) =>
    Math.abs(data[i] - sr) <= tol &&
    Math.abs(data[i + 1] - sg) <= tol &&
    Math.abs(data[i + 2] - sb) <= tol &&
    Math.abs(data[i + 3] - sa) <= tol;

  const set = (i) => { data[i] = fr; data[i + 1] = fg; data[i + 2] = fb; data[i + 3] = 255; };

  const visited = new Uint8Array(w * h);
  const stack = [[startX, startY]];

  while (stack.length) {
    let [cx, cy] = stack.pop();
    let idx = cy * w + cx;
    if (visited[idx]) continue;

    // Scan left to find the left boundary
    while (cx > 0 && match((cy * w + cx - 1) * 4) && !visited[cy * w + cx - 1]) cx--;

    let spanAbove = false, spanBelow = false;

    while (cx < w) {
      idx = cy * w + cx;
      const di = idx * 4;
      if (!match(di) || visited[idx]) break;

      visited[idx] = 1;
      set(di);

      if (cy > 0) {
        const ai = (cy - 1) * w + cx;
        if (match(ai * 4) && !visited[ai]) {
          if (!spanAbove) { stack.push([cx, cy - 1]); spanAbove = true; }
        } else { spanAbove = false; }
      }
      if (cy < h - 1) {
        const bi = (cy + 1) * w + cx;
        if (match(bi * 4) && !visited[bi]) {
          if (!spanBelow) { stack.push([cx, cy + 1]); spanBelow = true; }
        } else { spanBelow = false; }
      }
      cx++;
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

// Logo component — large, no container
const WillowLogo = ({ size = "h-10" }) => (
  <img src={LOGO_URL} alt="Willow Notes" className={`${size} object-contain`} />
);

export default function Drawings() {
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  
  const [drawings, setDrawings] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState(TOOLS[0]);
  const [currentColor, setCurrentColor] = useState('#2D3436');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [drawingTitle, setDrawingTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [viewingDrawing, setViewingDrawing] = useState(null);
  const [canvasReady, setCanvasReady] = useState(false);
  const [orientation, setOrientation] = useState('landscape');
  const [fillMode, setFillMode] = useState(false);
  const prevToolRef = useRef(TOOLS[0]);
  const usedColorsRef = useRef(new Set());

  const canvasWidth = orientation === 'landscape' ? 800 : 500;
  const canvasHeight = orientation === 'landscape' ? 500 : 700;

  // Canvas ref callback - initializes canvas when mounted
  const setCanvasRef = useCallback((canvas) => {
    if (canvas && canvasOpen) {
      canvasRef.current = canvas;
      
      // Set canvas internal dimensions
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      const context = canvas.getContext('2d');
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvasWidth, canvasHeight);
      context.lineCap = 'round';
      context.lineJoin = 'round';
      contextRef.current = context;
      setCanvasReady(true);
    }
  }, [canvasOpen, backgroundColor, canvasWidth, canvasHeight]);

  useEffect(() => {
    fetchDrawings();
  }, []);

  useEffect(() => {
    if (!canvasOpen) {
      setCanvasReady(false);
      contextRef.current = null;
    }
  }, [canvasOpen]);

  useEffect(() => {
    // Reinit canvas when background changes
    if (canvasReady && canvasRef.current && contextRef.current) {
      const context = contextRef.current;
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, [backgroundColor, canvasReady]);

  const fetchDrawings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/drawings`, { withCredentials: true });
      setDrawings(response.data);
    } catch (error) {
      console.error('Failed to fetch drawings:', error);
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, canvas.width, canvas.height);
  };

  const startDrawing = (e) => {
    if (!contextRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    // Fill mode — flood fill on click
    if (fillMode) {
      floodFill(canvas, Math.round(x), Math.round(y), currentColor);
      usedColorsRef.current.add(currentColor);
      return;
    }
    
    contextRef.current.beginPath();
    contextRef.current.moveTo(x, y);
    setIsDrawing(true);
    // Track color usage for "Rainbow Parrot" sticker
    if (currentTool.name !== 'Eraser') {
      usedColorsRef.current.add(currentColor);
    }
  };

  const draw = (e) => {
    if (!isDrawing || !contextRef.current || !canvasRef.current || fillMode) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    
    const context = contextRef.current;
    
    if (currentTool.id === 'eraser') {
      context.globalAlpha = 1;
      context.strokeStyle = backgroundColor;
    } else {
      context.globalAlpha = currentTool.opacity;
      context.strokeStyle = currentColor;
    }
    
    context.lineWidth = currentTool.lineWidth;
    context.lineCap = currentTool.lineCap;
    
    // Add texture for crayon
    if (currentTool.id === 'crayon') {
      context.lineWidth = currentTool.lineWidth + Math.random() * 4;
    }
    
    context.lineTo(x, y);
    context.stroke();
    
    // Keep path open for continuous drawing
    context.beginPath();
    context.moveTo(x, y);
  };

  const stopDrawing = () => {
    if (!contextRef.current) return;
    contextRef.current.closePath();
    contextRef.current.globalAlpha = 1;
    setIsDrawing(false);
  };

  const saveDrawing = async () => {
    if (!drawingTitle.trim()) {
      toast.error('Please give your drawing a name!');
      return;
    }

    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const imageData = canvas.toDataURL('image/png');
      const usedAllColors = usedColorsRef.current.size >= 8;
      
      await axios.post(
        `${API_URL}/api/drawings`,
        { 
          title: drawingTitle, 
          image_data: imageData, 
          background_color: backgroundColor,
          orientation,
          used_all_colors: usedAllColors,
        },
        { withCredentials: true }
      );
      
      toast.success('Drawing saved!');
      setCanvasOpen(false);
      setDrawingTitle('');
      usedColorsRef.current = new Set();
      fetchDrawings();
    } catch (error) {
      toast.error('Failed to save drawing');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteDrawing = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/drawings/${id}`, { withCredentials: true });
      toast.success('Drawing deleted!');
      fetchDrawings();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const downloadDrawing = (drawing) => {
    const link = document.createElement('a');
    link.download = `${drawing.title}.png`;
    link.href = drawing.image_data;
    link.click();
  };

  const handleLogout = async () => {
    try {
      await axios.delete(`${API_URL}/api/users/active`, { withCredentials: true });
    } catch (error) {
      console.error('Failed to leave active users:', error);
    }
    await logout();
    navigate('/login');
  };

  // Touch support
  const handleTouchStart = (e) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const touch = e.touches[0];
    const mockEvent = { clientX: touch.clientX, clientY: touch.clientY };
    startDrawing(mockEvent);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const touch = e.touches[0];
    const mockEvent = { clientX: touch.clientX, clientY: touch.clientY };
    draw(mockEvent);
  };

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <Toaster position="top-center" richColors />
      <SettingsGear />
      
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b-[1.5px] border-[var(--border-soft)] px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WillowLogo size="h-24" />
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors"
              data-testid="nav-diary"
            >
              <Book className="w-4 h-4 inline mr-1" /> {t('diary')}
            </button>
            <button
              className="px-5 py-2 rounded-full bg-[var(--c3)] border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A]"
              data-testid="nav-drawings"
            >
              <Pencil className="w-4 h-4 inline mr-1" /> {t('drawings')}
            </button>
            <button
              onClick={() => navigate('/stickers')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors"
              data-testid="nav-stickers"
            >
              <Award className="w-4 h-4 inline mr-1" /> {t('awards')}
            </button>
            <button
              onClick={() => navigate('/leaderboard')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors"
              data-testid="nav-leaderboard"
            >
              <Trophy className="w-4 h-4 inline mr-1" /> Leaderboard
            </button>
          </div>
          <div className="flex items-center gap-4">
            {user?.avatar && (
              <img src={`/avatars/${user.avatar}.png`} alt="avatar" className="w-8 h-8 rounded-full border-[1.5px] border-[var(--border-soft)] object-cover" data-testid="navbar-avatar" />
            )}
            <span className="text-[#1A1A1A] font-semibold hidden sm:inline">{t('hi')}, {user?.name}!</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-full border-[1.5px] border-[var(--border-soft)] shadow-sm bg-white hover:bg-[var(--c1)] text-[#1A1A1A] font-heading font-bold text-base transition-all"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 inline mr-1" /> {t('logout')}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-4xl font-heading font-bold text-[#1A1A1A]">
              My Drawings
            </h2>
            <p className="text-[#4A5568] mt-1 font-semibold">Create and save your artwork!</p>
          </div>
          <button
            onClick={() => { setCanvasOpen(true); setDrawingTitle(''); usedColorsRef.current = new Set(); }}
            className="soft-btn bg-[var(--c3)] hover:bg-[var(--c3-hover)] text-[#1A1A1A] px-6 py-3"
            data-testid="new-drawing-button"
          >
            <Plus className="w-5 h-5 inline mr-1" /> New Drawing
          </button>
        </div>

        {/* Drawings Grid */}
        {drawings.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-[var(--c3)] border-[1.5px] border-[var(--border-soft)] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
              <Pencil className="w-12 h-12 text-[#1A1A1A]" />
            </div>
            <h3 className="text-3xl font-heading font-bold text-[#1A1A1A] mb-2">No drawings yet!</h3>
            <p className="text-[#4A5568] mb-6 font-semibold">Start creating your first masterpiece</p>
            <button
              onClick={() => setCanvasOpen(true)}
              className="soft-btn bg-[var(--c3)] hover:bg-[var(--c3-hover)] text-[#1A1A1A] px-6 py-3"
            >
              <Plus className="w-4 h-4 inline mr-1" /> Create Drawing
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {drawings.map((drawing) => (
              <div
                key={drawing.id}
                className="bg-white rounded-xl border-[1.5px] border-[var(--border-soft)] overflow-hidden shadow-sm hover:shadow-md hover:translate-y-[-2px] transition-all group cursor-pointer"
                style={{ transform: `rotate(${(Math.random() - 0.5) * 3}deg)` }}
                onClick={() => setViewingDrawing(drawing)}
                data-testid={`drawing-${drawing.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden border-b-[1.5px] border-[var(--border-soft)]" style={{ backgroundColor: drawing.background_color }}>
                  <img
                    src={drawing.image_data}
                    alt={drawing.title}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-3 flex items-center justify-between">
                  <span className="font-bold text-[#1A1A1A] truncate">{drawing.title}</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadDrawing(drawing); }}
                      className="p-1.5 rounded-full hover:bg-[#F1F5F9] text-[#64748B]"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteDrawing(drawing.id); }}
                      className="p-1.5 rounded-full hover:bg-pink-50 text-[#64748B] hover:text-pink-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Canvas Dialog */}
        <Dialog open={canvasOpen} onOpenChange={setCanvasOpen}>
          <DialogContent className="max-w-5xl w-[95vw] max-h-[95vh] p-0 rounded-2xl overflow-y-auto border-[1.5px] border-[var(--border-soft)] shadow-lg" aria-describedby="canvas-description">
            <DialogHeader className="p-4 border-b-[1.5px] border-[var(--border-soft)]">
              <DialogTitle className="font-heading text-2xl font-bold flex items-center gap-2">
                <Pencil className="w-5 h-5 text-[#1A1A1A]" />
                Create New Drawing
              </DialogTitle>
              <p id="canvas-description" className="text-sm text-[#4A5568] font-medium">Use the tools below to draw!</p>
            </DialogHeader>
            
            <div className="p-4">
              {/* Title Input */}
              <div className="mb-4">
                <Input
                  value={drawingTitle}
                  onChange={(e) => setDrawingTitle(e.target.value)}
                  placeholder="Name your masterpiece..."
                  className="rounded-xl border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] focus:shadow-none"
                  data-testid="drawing-title-input"
                />
              </div>

              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-[var(--page-bg)] rounded-xl border-[1.5px] border-[var(--border-soft)]">
                {/* Tools */}
                <div className="flex gap-1">
                  {TOOLS.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => { setCurrentTool(tool); setFillMode(false); }}
                      className={`p-2.5 rounded-xl transition-all border-[1.5px] ${
                        currentTool.id === tool.id && !fillMode
                          ? 'bg-[var(--c3)] text-[#1A1A1A] border-[var(--c3-hover)] shadow-sm'
                          : 'bg-white text-[#4A5568] border-transparent hover:border-[var(--border-soft)]'
                      }`}
                      title={tool.name}
                      data-testid={`tool-${tool.id}`}
                    >
                      <tool.icon className="w-5 h-5" />
                    </button>
                  ))}
                  {/* Fill (Paint Bucket) Tool */}
                  <button
                    onClick={() => {
                      if (fillMode) {
                        setFillMode(false);
                      } else {
                        prevToolRef.current = currentTool;
                        setFillMode(true);
                      }
                    }}
                    className={`p-2.5 rounded-xl transition-all border-[1.5px] ${
                      fillMode
                        ? 'bg-[var(--c3)] text-[#1A1A1A] border-[var(--c3-hover)] shadow-sm'
                        : 'bg-white text-[#4A5568] border-transparent hover:border-[var(--border-soft)]'
                    }`}
                    title={fillMode ? 'Fill tool (active — click to deactivate)' : 'Fill tool'}
                    data-testid="tool-fill"
                  >
                    {fillMode
                      ? <BucketSpillIcon className="w-5 h-5" spillColor={currentColor} />
                      : <BucketIcon className="w-5 h-5" />
                    }
                  </button>
                </div>

                <div className="w-px h-8 bg-[var(--border-soft)]" />

                {/* Color Picker */}
                <div className="relative">
                  <button
                    onClick={() => { setShowColorPicker(!showColorPicker); setShowBgColorPicker(false); }}
                    className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl border-[1.5px] border-[var(--border-soft)] hover:bg-[var(--page-bg)]"
                    data-testid="color-picker-button"
                  >
                    <div className="w-6 h-6 rounded-full border-[1.5px] border-[var(--border-soft)]" style={{ backgroundColor: currentColor }} />
                    <span className="text-sm text-[#1A1A1A] font-bold">Color</span>
                  </button>
                  {showColorPicker && (
                    <div className="absolute top-full left-0 mt-2 p-3 bg-white rounded-xl border-[1.5px] border-[var(--border-soft)] shadow-lg z-10">
                      <div className="grid grid-cols-5 gap-2">
                        {COLORS.map((color) => (
                          <button
                            key={color}
                            onClick={() => { setCurrentColor(color); setShowColorPicker(false); }}
                            className={`w-8 h-8 rounded-full border-[1.5px] border-[var(--border-soft)] transition-transform hover:scale-110 ${
                              currentColor === color ? 'ring-2 ring-[var(--c3-hover)] ring-offset-1' : ''
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Background Color Picker */}
                <div className="relative">
                  <button
                    onClick={() => { setShowBgColorPicker(!showBgColorPicker); setShowColorPicker(false); }}
                    className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl border-[1.5px] border-[var(--border-soft)] hover:bg-[var(--page-bg)]"
                    data-testid="bg-color-picker-button"
                  >
                    <Palette className="w-5 h-5 text-[#1A1A1A]" />
                    <div className="w-5 h-5 rounded border-[1.5px] border-[var(--border-soft)]" style={{ backgroundColor: backgroundColor }} />
                    <span className="text-sm text-[#1A1A1A] font-bold">Background</span>
                  </button>
                  {showBgColorPicker && (
                    <div className="absolute top-full left-0 mt-2 p-3 bg-white rounded-xl border-[1.5px] border-[var(--border-soft)] shadow-lg z-10">
                      <div className="grid grid-cols-5 gap-2">
                        {BG_COLORS.map((color) => (
                          <button
                            key={color}
                            onClick={() => { setBackgroundColor(color); setShowBgColorPicker(false); }}
                            className={`w-8 h-8 rounded-lg border-[1.5px] border-[var(--border-soft)] transition-transform hover:scale-110 ${
                              backgroundColor === color ? 'ring-2 ring-[var(--c3-hover)] ring-offset-1' : ''
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="w-px h-8 bg-[var(--border-soft)]" />

                {/* Orientation Toggle */}
                <div className="flex gap-1 items-center">
                  <button
                    onClick={() => { setOrientation('landscape'); setCanvasOpen(false); setTimeout(() => setCanvasOpen(true), 50); }}
                    className={`p-2 rounded-lg transition-all border-[1.5px] ${orientation === 'landscape' ? 'bg-[var(--c3)] border-[var(--c3-hover)]' : 'bg-white border-transparent hover:border-[var(--border-soft)]'}`}
                    title="Landscape"
                    data-testid="orientation-landscape"
                  >
                    <RectangleHorizontal className="w-4 h-4 text-[#1A1A1A]" />
                  </button>
                  <button
                    onClick={() => { setOrientation('portrait'); setCanvasOpen(false); setTimeout(() => setCanvasOpen(true), 50); }}
                    className={`p-2 rounded-lg transition-all border-[1.5px] ${orientation === 'portrait' ? 'bg-[var(--c3)] border-[var(--c3-hover)]' : 'bg-white border-transparent hover:border-[var(--border-soft)]'}`}
                    title="Portrait"
                    data-testid="orientation-portrait"
                  >
                    <RectangleVertical className="w-4 h-4 text-[#1A1A1A]" />
                  </button>
                </div>

                <div className="w-px h-8 bg-[var(--border-soft)]" />

                {/* Clear Canvas */}
                <button
                  onClick={clearCanvas}
                  className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl border-[1.5px] border-[var(--border-soft)] hover:bg-[var(--c1)] text-[#1A1A1A]"
                  data-testid="clear-canvas-button"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="text-sm font-bold">Clear</span>
                </button>
              </div>

              {/* Canvas */}
              <div className="flex justify-center mb-4 overflow-auto">
                <canvas
                  ref={setCanvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={stopDrawing}
                  className={`border-[1.5px] border-[var(--border-soft)] rounded-xl shadow-sm bg-white ${fillMode ? 'cursor-cell' : 'cursor-crosshair'}`}
                  style={{ width: `${canvasWidth}px`, height: `${canvasHeight}px`, touchAction: 'none' }}
                  data-testid="drawing-canvas"
                />
              </div>

              {/* Save Button */}
              <div className="flex gap-3">
                <button
                  onClick={() => setCanvasOpen(false)}
                  className="flex-1 soft-btn bg-white hover:bg-[#F1F5F9] text-[#1A1A1A] py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={saveDrawing}
                  disabled={saving}
                  className="flex-1 soft-btn bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] py-3 disabled:opacity-50"
                  data-testid="save-drawing-button"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Save className="w-5 h-5" /> Save Drawing
                    </span>
                  )}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Drawing Dialog */}
        <Dialog open={!!viewingDrawing} onOpenChange={() => setViewingDrawing(null)}>
          <DialogContent className="max-w-3xl p-0 rounded-2xl overflow-hidden border-[1.5px] border-[var(--border-soft)] shadow-lg" aria-describedby="view-drawing-description">
            {viewingDrawing && (
              <>
                <DialogHeader className="p-4 border-b-[1.5px] border-[var(--border-soft)]">
                  <DialogTitle className="font-heading text-2xl font-bold">{viewingDrawing.title}</DialogTitle>
                  <p id="view-drawing-description" className="text-sm text-[#4A5568] font-medium">
                    Created on {new Date(viewingDrawing.created_at).toLocaleDateString()}
                  </p>
                </DialogHeader>
                <div className="p-4" style={{ backgroundColor: viewingDrawing.background_color }}>
                  <img
                    src={viewingDrawing.image_data}
                    alt={viewingDrawing.title}
                    className="w-full rounded-xl border-[1.5px] border-[var(--border-soft)]"
                  />
                </div>
                <div className="p-4 flex gap-3">
                  <button
                    onClick={() => downloadDrawing(viewingDrawing)}
                    className="flex-1 soft-btn bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] py-3"
                  >
                    <Download className="w-4 h-4 inline mr-1" /> Download
                  </button>
                  <button
                    onClick={() => { deleteDrawing(viewingDrawing.id); setViewingDrawing(null); }}
                    className="soft-btn bg-[var(--c1)] hover:bg-[var(--c1-hover)] text-[#1A1A1A] py-3 px-6"
                  >
                    <Trash2 className="w-4 h-4 inline mr-1" /> Delete
                  </button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
